import React from 'react';
import { useTranslation } from 'react-i18next';
import './PortalTile.css';

export interface PortalTileProps {
  /** Portal name shown below the badge */
  label: string;
  /** 24×24 icon exported from Figma */
  icon: React.ReactNode;
  /** Called when the tile is activated (click or keyboard) */
  onSelect?: () => void;
  /** When true the tile is inert (portal not integrated yet) */
  disabled?: boolean;
  /** Optional badge rendered below the label (e.g. `<ComingSoonBadge />`) */
  badge?: React.ReactNode;
  /** Custom className */
  className?: string;
  /** Custom test id (defaults to `portal-tile`) */
  'data-testid'?: string;
}

/**
 * PortalTile
 *
 * Single tile of the header portals grid: a round icon badge with the portal
 * name underneath. The whole tile is the click target. Figma nodes
 * `1235:9014`, `1235:9018` and `1235:9022` (all identical "icon+text" frames).
 *
 * The `disabled` variant reuses the "unavailable platform" treatment of the new
 * home cards — `#f4f4f4` badge, `#b3b3b3` glyph and label, plus a
 * `ComingSoonBadge` — instead of the enabled tile the (outdated) Figma grid
 * still shows for a portal that is not integrated yet.
 *
 * @example
 * ```tsx
 * <PortalTile
 *   label={t('Data Trust Platform')}
 *   icon={<ManageSearchIcon width={24} height={24} />}
 *   onSelect={() => navigateTo({ url: PDT_LINKS.HOME })}
 * />
 * ```
 *
 * @example Unavailable portal
 * ```tsx
 * <PortalTile
 *   label={t('idwall Portal')}
 *   icon={<ArrowSplitDisabledIcon width={24} height={24} />}
 *   badge={<ComingSoonBadge />}
 *   disabled
 * />
 * ```
 *
 * @since 1.0.0
 * @category Molecules
 */
export const PortalTile: React.FC<PortalTileProps> = ({
  label,
  icon,
  onSelect,
  disabled = false,
  badge,
  className = '',
  'data-testid': testId = 'portal-tile',
}) => {
  const { t } = useTranslation();

  const modifier = disabled ? 'portal-tile--disabled' : '';

  return (
    <button
      type="button"
      className={`portal-tile ${modifier} ${className}`
        .replace(/\s+/g, ' ')
        .trim()}
      onClick={disabled ? undefined : onSelect}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      aria-label={
        disabled ? t('Available soon') : t('Open {{portal}}', { portal: label })
      }
      data-testid={testId}
    >
      <span className="portal-tile__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="portal-tile__text">
        <span className="portal-tile__label">{label}</span>
        {badge}
      </span>
    </button>
  );
};

export default PortalTile;
